import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Link2, Loader2, ListChecks, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CambioPassword, GestioneUtenti } from "@/components/AccountSettings";
import CampiAnagrafica from "@/components/CampiAnagrafica";
import SchemaTipiMigrazione from "@/components/SchemaTipiMigrazione";
import GruppiTipiConfig from "@/components/GruppiTipiConfig";
import DocumentiImpostazioni from "@/components/DocumentiImpostazioni";
import ReferentiManager from "@/components/ReferentiManager";
import COIManager from "@/components/COIManager";
import DiagnosticaAI from "@/components/DiagnosticaAI";
import DoppioniDrive from "@/components/DoppioniDrive";
import IncassiDoppi from "@/components/IncassiDoppi";
import Sezione from "@/components/Sezione";

export default function Impostazioni() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [params] = useSearchParams();
  const { data: status } = useQuery({ queryKey: ["gstatus"], queryFn: async () => (await api.get("/google/status")).data });
  const { data: studio } = useQuery({ queryKey: ["google-studio"], queryFn: async () => (await api.get("/google/studio")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [driveRoot, setDriveRoot] = useState("");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => { if (settings?.drive_root) setDriveRoot(settings.drive_root); }, [settings]);
  useEffect(() => { if (params.get("connected")) toast.success("Google Workspace collegato"); }, [params]);

  const connect = async () => {
    setConnecting(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirect = window.location.origin + "/auth/google";
      const { data } = await api.get("/auth/google/url", { params: { redirect_uri: redirect } });
      window.location.href = data.url;
    } catch { toast.error("Errore avvio connessione"); setConnecting(false); }
  };

  const disconnect = async () => {
    await api.post("/google/disconnect");
    qc.invalidateQueries({ queryKey: ["gstatus"] });
    toast.success("Google scollegato");
  };

  const saveDrive = async () => {
    await api.put("/settings", { drive_root: driveRoot });
    qc.invalidateQueries({ queryKey: ["settings"] });
    qc.invalidateQueries({ queryKey: ["gstatus"] });
    toast.success("Cartella Drive salvata");
  };

  const connected = status?.connected;

  return (
    <div data-testid="impostazioni-page" id="impostazioni"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <header className="flex flex-col gap-0.5">
          <h1 className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B]">Impostazioni</h1>
          <p className="text-[14px] md:text-[16px] text-[#4A4A48]">
            Collegamenti, cartelle, tipi di pratica, documenti e utenti dello studio
          </p>
        </header>

        <Sezione testid="sez-google" titolo="Google Workspace"
          azioni={<span className={`text-[13px] ${connected ? "text-[#D8E6D2]" : "text-[#E7D9B2]"}`}>
            {connected ? "collegato" : "non collegato"}
          </span>}>
          {connected ? (
            <>
              <p className="text-[14px] text-[#1A1A1A] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#2E5E3A]" /> Collegato come <b>{status.google_email}</b>
              </p>
              <p className="text-[14px] text-[#4A4A48]">
                Drive, Gmail (solo bozze e anteprima) e Calendar sono attivi per l'assistente.
              </p>
              <div className="flex flex-wrap gap-2.5">
                <button data-testid="google-reconnect" onClick={connect} disabled={connecting} className="btn btn-p btn-larghezza sm:w-auto">
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Aggiorna i permessi
                </button>
                <button data-testid="google-disconnect" onClick={disconnect} className="btn btn-o btn-larghezza sm:w-auto">Scollega</button>
              </div>
              <div className="nota-tenue">
                Usa «Aggiorna i permessi» per abilitare l'anteprima delle email in arrivo nella Home.
              </div>
            </>
          ) : (
            <>
              <p className="text-[14px] text-[#4A4A48]">
                Collega l'account dello studio per salvare i documenti su Drive, preparare bozze email e promemoria in Calendar.
              </p>
              <button data-testid="google-connect" onClick={connect} disabled={connecting} className="btn btn-g btn-larghezza sm:w-auto">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Collega Google
              </button>
            </>
          )}
        </Sezione>

        <Sezione testid="google-studio-box" titolo="Account dello studio (Drive clienti)">
          <p className="text-[14px] text-[#4A4A48]">
            Le cartelle e i documenti dei clienti vengono <b>sempre</b> salvati sul Drive di questo account,
            chiunque sia l'utente collegato. Gmail e Calendar restano invece personali di ciascun utente.
          </p>
          <div className="sub">
            <div className="riga" data-testid="google-studio-current">
              <span className="eti w-[120px] shrink-0">Account attuale</span>
              <span className="flex-grow">
                <b>{studio?.google_email || "nessun account collegato"}</b>
                {studio?.nome ? ` (${studio.nome})` : ""}{studio?.sono_io ? " — sei tu" : ""}
              </span>
            </div>
            {can("gestione_utenti") && (studio?.utenti || []).length > 0 && (
              <div className="riga">
                <span className="eti w-[120px] shrink-0">Cambia account</span>
                <select data-testid="google-studio-select" defaultValue={studio?.user_id || ""}
                  onChange={async (e) => {
                    if (!e.target.value) return;
                    try {
                      await api.put("/google/studio", { user_id: e.target.value });
                      toast.success("Account dello studio aggiornato");
                      qc.invalidateQueries({ queryKey: ["google-studio"] });
                    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
                  }}
                  className="campo flex-grow">
                  {(studio.utenti || []).map((u) => (
                    <option key={u.id} value={u.id} disabled={!u.collegato}>
                      {u.nome} {u.google_email ? `· ${u.google_email}` : "· Google non collegato"}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Sezione>

        {can("gestione_impostazioni") && (
          <Sezione testid="sez-drive-root" titolo="Cartella Drive principale">
            <p className="text-[14px] text-[#4A4A48]">
              Incolla il link (o l'ID) della cartella condivisa. L'app creerà al suo interno una
              sottocartella per ogni cliente, con Contratti e Contabilità.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <input data-testid="drive-root-input" value={driveRoot} onChange={(e) => setDriveRoot(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/…" className="campo flex-grow" />
              <button data-testid="drive-root-save" onClick={saveDrive} className="btn btn-p btn-larghezza sm:w-auto">Salva</button>
            </div>
          </Sezione>
        )}

        {can("gestione_impostazioni") && (
          <Link to="/tipi-pratica" data-testid="tipi-pratica-link"
            className="sez px-5 py-4 flex items-center gap-4 no-underline text-[#1A1A1A] hover:border-[#A8863F] transition-colors group">
            <ListChecks className="w-5 h-5 text-[#A8863F] shrink-0" />
            <span className="flex-grow">
              <span className="block text-[16px] font-bold text-[#1B2A4B]">Tipi di pratica</span>
              <span className="block text-[14px] text-[#4A4A48]">
                I modelli di pratica con i loro step, usati quando apri una pratica nuova.
              </span>
            </span>
            <ChevronRight className="w-5 h-5 text-[#5A5A5A] group-hover:text-[#A8863F] transition-colors" />
          </Link>
        )}

        {can("gestione_impostazioni") && <CampiAnagrafica />}
        {can("gestione_impostazioni") && <DocumentiImpostazioni />}
        {can("gestione_impostazioni") && <GruppiTipiConfig />}
        {can("gestione_utenti") && <SchemaTipiMigrazione />}
        {can("gestione_impostazioni") && <ReferentiManager />}
        {can("gestione_impostazioni") && <COIManager />}
        {can("gestione_impostazioni") && <IncassiDoppi />}
        {can("gestione_impostazioni") && <DoppioniDrive />}
        {can("gestione_impostazioni") && <DiagnosticaAI />}
        <CambioPassword />
        {can("gestione_utenti") && <GestioneUtenti />}
      </div>

      {/* I pannelli di amministrazione sono componenti storici: qui si allineano a carte, bordi e titoli delle altre pagine */}
      <style>{`
        #impostazioni .bg-white { border-radius: 6px; }
        #impostazioni [class*="border-[#1A2B4A]"] { border-color: #E2E0D9; }
        #impostazioni .mb-5, #impostazioni .mb-6, #impostazioni .mt-6 { margin-top: 0; margin-bottom: 0; }
      `}</style>
    </div>
  );
}
